import { format, parse } from "date-fns";
import React from "react";
import { RangeModifier, DateUtils } from "react-day-picker";
import { EventDates } from "../types";
import { getSelectedAndAvailableDates } from "../utils";

let TOTAL_EVENT_CAPACITY = 20;
const trainingEventLocations: Record<string, string> = {
  "04/16/2025": "Las Vegas",
  "05/14/2025": "Ganjier Campus",
  "06/11/2025": "Ganjier Campus",
  "07/07/2025": "New Jersey",
  "08/06/2025": "Ganjier Campus",
  "09/01/2025": "Ganjier Campus",
  "09/03/2025": "Ganjier Campus",
  "09/29/2025": "Ganjier Campus",
  "10/01/2025": "Ganjier Campus",
};

const examEventLocations: Record<string, string> = {
  "04/18/2025": "Las Vegas",
  // "04/19/2025": "Las Vegas",
  "05/16/2025": "Ganjier Campus",
  "06/13/2025": "Ganjier Campus",
  "07/09/2025": "New Jersey",
  // "07/10/2025": "New Jersey",
  "08/08/2025": "Ganjier Campus",
  "09/05/2025": "Ganjier Campus",
  "10/03/2025": "Ganjier Campus",
};

// format a js Date object to MM/DD/YYYY format
function formatDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
}

// get location for a given date
function getLocation(date: Date): string {
  const formattedDate = formatDate(date);
  if (trainingEventLocations[formattedDate]) {
    return " @ " + trainingEventLocations[formattedDate];
  } else if (examEventLocations[formattedDate]) {
    return " @ " + examEventLocations[formattedDate];
  }

  return "";
}

function getBookingsOnDate(
  dates: EventDates["liveEvents"],
  selectedDate: Date,
) {
  return Object.entries(dates).find(([date]) =>
    DateUtils.isSameDay(parse(date, "MM/dd/yyyy", new Date()), selectedDate),
  )![1];
}

function getFormattedDates({ from, to }: RangeModifier & { from: Date }) {
  const formattedDays = [from, to]
    .filter(Boolean)
    .map((date) => format(date!, "dd"))
    .join("/");
  const formattedMonth = format(from, "MMMM");
  const formattedWeekdays = [from, to]
    .filter(Boolean)
    .map((date) => format(date!, "EEE"))
    .join("/");

  return { formattedDays, formattedMonth, formattedWeekdays };
}

export function SelectedDatePanel({
  dates,
  selectedDates,
  onConfirm,
}: {
  selectedDates: RangeModifier & { from: Date };
  dates: EventDates;
  onConfirm: () => void;
}) {
  const { availableDates } = getSelectedAndAvailableDates(dates);
  const firstDate = selectedDates.from;
  const isAvailable = Boolean(
    availableDates.find(([date]) => DateUtils.isSameDay(date, firstDate)),
  );
  const isLiveEvent = Boolean(
    Object.keys(dates.liveEvents).find((date) =>
      DateUtils.isSameDay(parse(date, "MM/dd/yyyy", new Date()), firstDate),
    ),
  );

  const { formattedDays, formattedMonth, formattedWeekdays } =
    getFormattedDates(selectedDates);

  let title = isLiveEvent ? "Ganjier Live Training" : "Ganjier Exam";
  // Check if first date is defined
  TOTAL_EVENT_CAPACITY = 20;

  if (firstDate) {
    title += getLocation(firstDate);
    // Check if title contains New Jersey or Vegas
    if (title.includes("New Jersey") || title.includes("Las Vegas")) {
      console.log("setting it to 24");
      TOTAL_EVENT_CAPACITY = 24;
    }
  }

  return (
    <div>
      <div className="GanjierCalendar-description-Date">
        <div className="GanjierCalendar-description-Day">{formattedDays}</div>
        <div>
          <div className="GanjierCalendar-description-Month">
            {formattedMonth}
          </div>
          <div className="GanjierCalendar-description-Weekday">
            {formattedWeekdays}
          </div>
        </div>
      </div>
      <div className="GanjierCalendar-description-Banner">
        {isAvailable ? <CheckCircle /> : <CrossCircle />}
        <div className="GanjierCalendar-description-Type">{title}</div>
      </div>
      <div className="GanjierCalendar-description-Body">
        {!isAvailable ? (
          <p>All the available spaces have been taken on this day</p>
        ) : (
          <>
            <p>
              <span className="GanjierCalendar-description-SeatCount">
                {TOTAL_EVENT_CAPACITY -
                  (isLiveEvent
                    ? getBookingsOnDate(dates.liveEvents, selectedDates.from)
                    : getBookingsOnDate(dates.exams, selectedDates.from))}
              </span>{" "}
              spaces are still available for this day. Book now to secure a
              space.
            </p>
            <button onClick={onConfirm}>Secure a Space</button>
          </>
        )}
      </div>
    </div>
  );
}

function CheckCircle() {
  return (
    <svg width="43" height="42" viewBox="0 0 43 42">
      <g fill="none" stroke="#67961b" strokeWidth="3">
        <ellipse cx="21.5" cy="21" rx="21.5" ry="21" stroke="none" />
        <ellipse cx="21.5" cy="21" rx="20" ry="19.5" fill="none" />
      </g>
      <path
        d="M18.237,0,4.4,13.511-2.007,7.558"
        transform="translate(13.385 14.244)"
        fill="none"
        stroke="#67961b"
        strokeWidth="3"
      />
    </svg>
  );
}

function CrossCircle() {
  return (
    <svg width="43" height="42" viewBox="0 0 43 42">
      <g
        transform="translate(0 0)"
        fill="none"
        stroke="#ad1c1c"
        strokeWidth="3"
      >
        <circle cx="21" cy="21" r="21" stroke="none" />
        <circle cx="21" cy="21" r="19.5" fill="none" />
      </g>
      <line
        x2="17.465"
        y2="17.465"
        transform="translate(13.099 13.099)"
        fill="none"
        stroke="#ad1c1c"
        strokeWidth="3"
      />
      <line
        id="Line_2"
        data-name="Line 2"
        x1="17.465"
        y2="17.465"
        transform="translate(13.099 13.099)"
        fill="none"
        stroke="#ad1c1c"
        strokeWidth="3"
      />
    </svg>
  );
}
